import * as React from 'react';
import { ChevronDown } from 'lucide-react';

import {
  Select as SelectRoot,
  SelectTrigger as SelectTriggerBase,
  SelectValue,
  SelectIcon,
  SelectContent as SelectContentBase,
  SelectItem as SelectItemBase,
} from '@/hen-ui/shadcn/select';
import {
  selectTriggerVariants,
  type SelectTriggerVariantProps,
} from './select.variants';
import { cn } from '@/hen-ui/utils';

export interface SelectProps extends SelectTriggerVariantProps {
  /**
   * Error state - when true, displays error styling and sets aria-invalid
   */
  error?: boolean;
  /**
   * Error message text - when provided, sets aria-describedby and aria-invalid
   */
  errorMessage?: string;
  /**
   * Label text - when variant is 'two-line', this label will animate from placeholder to top position
   */
  label?: string;
  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;
  /**
   * Selected value
   */
  value?: string;
  /**
   * Default value
   */
  defaultValue?: string;
  /**
   * Callback when value changes
   */
  onValueChange?: (value: string) => void;
  /**
   * Whether the select is disabled
   */
  disabled?: boolean;
  /**
   * Children should be SelectItem components
   */
  children?: React.ReactNode;
  /**
   * ID for the select trigger
   */
  id?: string;
  /**
   * ARIA described by
   */
  'aria-describedby'?: string;
  /**
   * ARIA invalid
   */
  'aria-invalid'?: string;
}

export interface SelectItemProps
  extends React.ComponentPropsWithoutRef<typeof SelectItemBase> {
  children: React.ReactNode;
}

/**
 * SelectItem component for use within Select
 */
export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectItemBase>,
  SelectItemProps
>(({ className, children, ...props }, ref) => (
  <SelectItemBase
    ref={ref}
    className={cn(
      'rounded-sm py-xxs px-sm text-b5',
      'focus:bg-neutral-100 focus:text-body',
      className,
    )}
    {...props}
  >
    {children}
  </SelectItemBase>
));
SelectItem.displayName = SelectItemBase.displayName;

/**
 * Select component following HEN Design System standards.
 *
 * A standardized select dropdown field with HEN-specific variants and design tokens.
 * All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // Basic usage
 * <Select placeholder="Select an option">
 *   <SelectItem value="1">Option 1</SelectItem>
 *   <SelectItem value="2">Option 2</SelectItem>
 * </Select>
 *
 * @example
 * // With label and controlled state
 * <label htmlFor="country-select">Country</label>
 * <Select id="country-select" value={value} onValueChange={setValue}>
 *   <SelectItem value="us">United States</SelectItem>
 *   <SelectItem value="ca">Canada</SelectItem>
 * </Select>
 *
 * @example
 * // With error state
 * <Select
 *   placeholder="Select option"
 *   error
 *   errorMessage="This field is required"
 * >
 *   <SelectItem value="1">Option 1</SelectItem>
 * </Select>
 *
 * @example
 * // Two-line label variant
 * <Select variant="two-line" label="Country">
 *   <SelectItem value="us">United States</SelectItem>
 *   <SelectItem value="ca">Canada</SelectItem>
 * </Select>
 */
export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      variant,
      error,
      errorMessage,
      label,
      placeholder = 'Select...',
      value,
      defaultValue,
      onValueChange,
      disabled,
      children,
      id,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
    },
    ref,
  ) => {
    // Generate unique id if not provided
    const selectId = React.useId();
    const finalId = id || selectId;
    const errorId = errorMessage ? `${finalId}-error` : undefined;
    const labelId = label ? `${finalId}-label` : undefined;

    // Track open and value state for two-line label
    const [isOpen, setIsOpen] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(
      Boolean(value || defaultValue),
    );

    // Update hasValue when controlled value changes
    React.useEffect(() => {
      if (value !== undefined) {
        setHasValue(Boolean(value));
      }
    }, [value]);

    // Determine if label should be floating (at top)
    const isFloating = variant === 'two-line' && (isOpen || hasValue);

    // Combine aria-describedby values
    const describedBy =
      [ariaDescribedBy, errorId].filter(Boolean).join(' ') || undefined;

    // Set aria-invalid based on error prop or errorMessage
    const isInvalid = error || Boolean(errorMessage);
    const finalAriaInvalid: 'true' | 'false' | undefined =
      ariaInvalid !== undefined
        ? (ariaInvalid as 'true' | 'false')
        : isInvalid
          ? 'true'
          : undefined;

    const handleValueChange = (newValue: string) => {
      setHasValue(Boolean(newValue));
      onValueChange?.(newValue);
    };

    // For two-line variant with label, don't show placeholder text
    const finalPlaceholder = variant === 'two-line' && label ? '' : placeholder;

    return (
      <>
        <div className='relative w-full'>
          <SelectRoot
            value={value}
            defaultValue={defaultValue}
            onValueChange={handleValueChange}
            disabled={disabled}
            onOpenChange={setIsOpen}
          >
            {variant === 'two-line' && label && (
              <label
                id={labelId}
                htmlFor={finalId}
                className={cn(
                  'absolute left-3 pointer-events-none',
                  'transition-all duration-200 ease-in-out',
                  'text-body font-body',
                  isFloating
                    ? 'top-[6px] text-l2 leading-[14px]'
                    : 'top-1/2 -translate-y-1/2 text-b5 leading-[22px]',
                  isInvalid && isFloating ? 'text-error-500' : '',
                  isFloating && !isInvalid ? 'text-body' : 'text-subtext',
                )}
              >
                {label}
              </label>
            )}
            <SelectTriggerBase
              ref={ref}
              id={finalId}
              className={cn(
                selectTriggerVariants({ variant, error: isInvalid, hasValue }),
              )}
              aria-invalid={finalAriaInvalid}
              aria-describedby={describedBy}
              aria-labelledby={
                variant === 'two-line' && label ? labelId : undefined
              }
            >
              <SelectValue placeholder={finalPlaceholder} />
              <SelectIcon asChild>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 text-black',
                    'transition-transform duration-200',
                    isOpen && 'rotate-180',
                    variant === 'two-line' && 'translate-y-[-4px]',
                  )}
                />
              </SelectIcon>
            </SelectTriggerBase>
            <SelectContentBase
              className={cn(
                'max-h-96 min-w-[8rem]',
                'rounded-sm border border-neutral-200 bg-white text-body shadow-md',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
                'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
              )}
              viewportClassName='p-1'
              position='popper'
              sideOffset={4}
            >
              {placeholder.trim() !== '' && (
                <i className='py-xxs px-sm mb-xxs text-b5 text-body'>
                  {placeholder}
                </i>
              )}
              {children}
            </SelectContentBase>
          </SelectRoot>
        </div>
        {errorMessage && (
          <div
            id={errorId}
            className='mt-2 text-sm text-error-500'
            role='alert'
            aria-live='polite'
          >
            {errorMessage}
          </div>
        )}
      </>
    );
  },
);

Select.displayName = 'Select';
